import { Menu } from '@mui/icons-material'
import { Box, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslations } from 'next-intl'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'

import { useSettings } from '@core/context/settingsContext'

import CriticalNotificationsOverlay from '~/app/notifications/components/CriticalNotificationsOverlay'
import NotificationsBell from '~/app/notifications/components/NotificationsBell'
import QuickSearchBar from '~/components/QuickSearch/QuickSearchBar'
import { SEARCH_INPUT_MAX_WIDTH } from '~/components/QuickSearch/constants'
import { useStatsigGate } from '~/configs/statsig'
import { useIsFullscreenMode } from '~/hooks/useFullscreenMode'
import UserDropdown from '~/layouts/components/UserDropdown'
import Banners from '~/layouts/components/banners'
import Navigation from '~/layouts/components/navigation'
import { useBannerOffset } from '~/store/slices/display'

type LayoutProps = PropsWithChildren<{
  hidden: boolean
}>

const Layout = (props: LayoutProps) => {
  const { hidden, children } = props
  const t = useTranslations('layouts.Layout')
  const { settings, saveSettings } = useSettings()
  const isCollapsed = useRef(settings.navCollapsed)
  const [navVisible, setNavVisible] = useState<boolean>(false)
  const { enabled: assistantBarEnabled } = useStatsigGate('ai-assistant')

  const toggleNavVisibility = () => setNavVisible(!navVisible)
  const bannerOffset = useBannerOffset()
  // Fullscreen ("monitor") mode drops the nav, top bar and content padding so the page
  // fills the screen — see `~/hooks/useFullscreenMode`. Banners stay put: the cloaking
  // banner is a safety signal about whose data you're looking at.
  const isFullscreen = useIsFullscreenMode()

  useEffect(() => {
    if (hidden) {
      if (settings.navCollapsed) {
        saveSettings({ ...settings, navCollapsed: false })
        isCollapsed.current = true
      }
    } else if (isCollapsed.current) {
      saveSettings({ ...settings, navCollapsed: true })
      isCollapsed.current = false
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden])

  return (
    <>
      <SkipLink
        href="#main-content"
        onClick={(e) => {
          e.preventDefault()
          document.getElementById('main-content')?.focus()
        }}
      >
        {t('skipToMainContent')}
      </SkipLink>

      {/* Timezone and cloaking banners */}
      {<Banners />}

      <LayoutWrapper className="layout-wrapper">
        {/* Not rendered at all in fullscreen: the drawer is `variant="permanent"` and so
            occupies flow width — dropping it reclaims the inset with no padding math. */}
        {isFullscreen ? null : (
          <Navigation
            navWidth={260}
            navVisible={navVisible}
            setNavVisible={setNavVisible}
            collapsedNavWidth={68}
            toggleNavVisibility={toggleNavVisibility}
            navigationBorderWidth={0}
            {...props}
          />
        )}
        <MainContentWrapper className="layout-content-wrapper" bannerOffset={bannerOffset}>
          {hidden && !isFullscreen ? (
            <MobileNavTrigger
              aria-label={t('openNavMenu')}
              data-cy="nav-menu"
              onClick={toggleNavVisibility}
              style={{ top: bannerOffset + 12 }}
            >
              <Menu />
            </MobileNavTrigger>
          ) : null}
          {isFullscreen ? null : <CriticalNotificationsOverlay />}
          {isFullscreen ? null : (
            <TopBar className="layout-top-bar" stickyTop={bannerOffset}>
              {/* CSS grid with two equal `1fr` flanks keeps the search pill
                centered relative to the TopBar (and therefore the visible
                page area) regardless of how wide the right-side controls
                are. The middle track grows up to `SEARCH_INPUT_MAX_WIDTH`
                so the full placeholder can render on wide viewports. */}
              <Box />
              <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                <QuickSearchBar />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <NotificationsBell settings={settings} />
                <UserDropdown settings={settings} />
              </Box>
            </TopBar>
          )}
          <ContentWrapper
            id="main-content"
            tabIndex={-1}
            className="layout-page-content"
            assistantBarEnabled={assistantBarEnabled}
            fullscreen={isFullscreen}
          >
            {children}
          </ContentWrapper>
        </MainContentWrapper>
      </LayoutWrapper>
    </>
  )
}

export default Layout

const LayoutWrapper = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  backgroundColor: theme.palette.background.layout
}))

type MainContentProps = {
  bannerOffset: number
}

const MainContentWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== 'bannerOffset' })<MainContentProps>(
  ({ bannerOffset }) => ({
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',

    // Leave space for the banners, but take up all that remains
    minHeight: `calc(100vh - ${bannerOffset}px)`,
    marginTop: bannerOffset
  })
)

type ContentWrapperProps = {
  assistantBarEnabled: boolean
  fullscreen: boolean
}

/**
 * Content inset while fullscreen, in theme spacing units (`spacing(4)` === 16px).
 *
 * Tighter than the standard `spacing(6)` (24px) so a wall display still gains real
 * estate, but deliberately not flush: at 0 the site selector's floating label was
 * clipped against the top of the viewport and the toolbar buttons collided with the
 * right edge. This is the single knob — tune it to taste.
 */
const FULLSCREEN_CONTENT_PADDING = 4

const ContentWrapper = styled('main', {
  shouldForwardProp: (prop) => prop !== 'assistantBarEnabled' && prop !== 'fullscreen'
})<ContentWrapperProps>(({ theme, assistantBarEnabled, fullscreen }) => ({
  flexGrow: 1,
  width: '100%',
  backgroundColor: theme.palette.background.layout,
  padding: theme.spacing(fullscreen ? FULLSCREEN_CONTENT_PADDING : 6),
  // The assistant bar is hidden in fullscreen, so its bottom allowance goes with it.
  paddingBottom: fullscreen
    ? theme.spacing(FULLSCREEN_CONTENT_PADDING)
    : assistantBarEnabled
      ? theme.spacing(12)
      : theme.spacing(6),
  transition: 'padding .25s ease-in-out',
  ...(fullscreen
    ? {}
    : {
        [theme.breakpoints.down('sm')]: {
          paddingLeft: theme.spacing(4),
          paddingRight: theme.spacing(4)
        }
      })
}))

// Slim top toolbar that hosts the centered Cmd+K search pill plus
// right-aligned account/notification controls. Sits above `ContentWrapper`
// and sticks just under the banner row (or to the top of the viewport when
// no banner is showing) as the page scrolls. The banner row is
// `position: fixed` with a higher z-index, so we offset `top` by the current
// banner height to keep the toolbar visible instead of slipping behind the
// banner. Layout is a 3-column grid `1fr | minmax(0, SEARCH_INPUT_MAX_WIDTH)
// | 1fr` so the equal `1fr` flanks keep the middle (search) column visually
// centered regardless of how much content the right column actually holds.
const TopBar = styled(Box, { shouldForwardProp: (prop) => prop !== 'stickyTop' })<{ stickyTop: number }>(
  ({ theme, stickyTop }) => ({
    position: 'sticky',
    top: stickyTop,
    zIndex: theme.zIndex.appBar,
    display: 'grid',
    gridTemplateColumns: `1fr minmax(0, ${SEARCH_INPUT_MAX_WIDTH}px) 1fr`,
    alignItems: 'center',
    minHeight: theme.mixins.toolbar.minHeight,
    padding: theme.spacing(0, 6),
    backgroundColor: theme.palette.background.layout,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4)
    }
  })
)

// Floating hamburger visible only when the sidebar is fully hidden on mobile/tablet.
// Replaces the old AppBar-hosted trigger.
const MobileNavTrigger = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  left: theme.spacing(3),
  zIndex: theme.zIndex.drawer - 1,
  width: 40,
  height: 40,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
  '&:hover': {
    backgroundColor: theme.palette.background.paper
  }
}))

const SkipLink = styled('a')(({ theme }) => ({
  position: 'absolute',
  left: -9999,
  top: 'auto',
  width: 1,
  height: 1,
  overflow: 'hidden',
  zIndex: -1,
  '&:focus': {
    position: 'fixed',
    top: 8,
    left: 8,
    width: 'auto',
    height: 'auto',
    padding: '8px 16px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    zIndex: 2000,
    borderRadius: 4,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 14,
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2
  }
}))
